/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

import { languages } from '../config';
import validate from './config';
import * as constants from './constants';

export const strings = {
  OPTION_NAMES_NO_WHITESPACES: "Option names can't begin with spaces.",
  OPTION_NAMES_CANT_BE_EMPTY: "Option names can't be empty.",
  MORE_THAN_64_CHARS: 'More than 64 characters.',
  MORE_THAN_X_CHARS: length => `More than ${length} characters.`,
  ONE_OR_MORE_THAN_64_CHARS:
    'One or more synonyms are more than 64 characters.',
  DUPLICATE_SYNONYMS: 'Duplicate synonym values.',
  DUPLICATE_VALUES: 'Duplicate option names.'
};

export const validateWebChatAllFields = (validationMappings = {}) => {
  const validations = Object.keys(validationMappings).map(field => {
    const el = document.querySelector(field);
    const value = (el as any)?.value || '';
    return validateWebChatField({
      field,
      value
    });
  });
  return validations;
};

export const validateWebChatField = (
  validationMappings = {},
  { field, value }: any = {}
) => {
  return validateIntegrationField(validationMappings, { field, value });
};

export const validateWhatsAppAllFields = (
  validationMappings = _ => ({}),
  integration
) => {
  const invalidFields = {};
  Object.keys(validationMappings(integration)).forEach(field => {
    const el = document.getElementById(field);
    const value = (el as any)?.value || '';
    const isValid = validateWhatsAppField(validationMappings(integration), {
      field,
      value
    });
    // If we don't find the element because it is not rendered -- we can't mark invalid.
    // This is hopefully a temporary solution as we refactor this.
    if (!el || isValid === true) {
      delete invalidFields[field];
    } else {
      invalidFields[field] = isValid;
    }
  });
  return invalidFields;
};

export const validateWhatsAppField = (
  validationMappings = {},
  { field, value }: any = {}
) => {
  return validateIntegrationField(validationMappings, { field, value });
};

export const validateIntegrationField = (
  validationMappings,
  { field, value }
) => {
  const validationFunctions = validationMappings[field];
  const validateEach = (functions, value) => {
    // return this first validation that does not return true
    const invalid = functions.find(func => func(value) !== true);
    // if none are returned, it means we are good to go, if not, get the message
    return invalid && typeof invalid == 'function' ? invalid(value) : true;
  };
  const validationStatus =
    validationFunctions &&
    Array.isArray(validationFunctions) &&
    validationFunctions.length > 0
      ? validateEach(validationFunctions, value)
      : true;
  return validationStatus;
};

export const validateIntegrationName = (
  validationMappings = {},
  { field, value }: any = {}
) => {
  const validationFunctions = validationMappings[field];
  const validateEach = (functions, value) => {
    // return this first validation that does not return true
    const invalid = functions.find(func => func(value) !== true);
    // if none are returned, it means we are good to go, if not, get the message
    return invalid && typeof invalid == 'function' ? invalid(value) : true;
  };
  const validationStatus =
    validationFunctions &&
    Array.isArray(validationFunctions) &&
    validationFunctions.length > 0
      ? validateEach(validationFunctions, value)
      : true;
  return validationStatus;
};

const LANGUAGES = languages.map(language => language.value);

const workspacesConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateWorkspace
  }
};

const workspaceConstraints = {
  name: {
    type: constants.STRING,
    length: {
      maximum: 64
    },
    format: {
      pattern: constants.NAME_REGEX,
      message: constants.NAME_PATTERN_ERROR
    }
  },
  description: {
    type: constants.STRING,
    length: {
      maximum: 128
    },
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  },
  language: {
    type: constants.STRING,
    inclusion: {
      within: LANGUAGES,
      message: '^Languages that are currently supported are'
    }
  },
  intents: {
    type: constants.ARRAY,
    each: {
      validator: validateIntent
    }
  },
  dialog_nodes: {
    type: constants.ARRAY,
    each: {
      validator: validateDialogNode
    }
  }
};

const intentsConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateIntent
  }
};

const intentConstraints = {
  intent: {
    presence: { message: ' is required.' },
    type: constants.STRING,
    length: {
      maximum: constants.MAX_INTENT_NAME_LENGTH
    },
    intentNameValidator: true
  },
  description: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_INTENT_DESCRIPTION_LENGTH
    },
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  },
  global: {
    type: constants.BOOLEAN
  },
  examples: {
    type: constants.ARRAY,
    each: {
      validator: validateExample
    }
  }
};

const calloutConstraints = {
  name: {
    presence: true,
    type: constants.STRING,
    length: {
      maximum: 256
    }
  },
  type: {
    presence: false,
    inclusion: {
      within: [
        'client',
        'server',
        'web_action',
        'cloud_function',
        'webhook',
        'assistant_action'
      ],
      message:
        'must be one of these values: client, server, web_action, cloud_function, webhook, assistant_action'
    }
  },
  result_variable: {
    presence: true,
    type: constants.STRING,
    length: {
      maximum: 64
    }
  }
};

const optionsConstraints = {
  uniqueItems: {
    message: strings.DUPLICATE_VALUES,
    keys: ['value']
  },
  type: constants.ARRAY,
  each: {
    validator: validateOptionListItem
  }
};

const optionConstraints = {
  value: {
    format: {
      pattern: /^[^\s]+.*$/,
      message: strings.OPTION_NAMES_NO_WHITESPACES
    },
    presence: {
      message: strings.OPTION_NAMES_CANT_BE_EMPTY
    },
    length: {
      maximum: constants.OPTION_VALUE_MAX_LENGTH,
      message: strings.MORE_THAN_64_CHARS
    }
  },
  synonyms: {
    presence: false,
    uniqueItems: {
      message: strings.DUPLICATE_SYNONYMS
    },
    type: constants.ARRAY,
    each: {
      validator: validateOptionSynonym
    }
  }
};

const optionSynonymConstraints = {
  length: {
    maximum: constants.OPTION_SYNONYM_MAX_LENGTH,
    message: strings.ONE_OR_MORE_THAN_64_CHARS
  },
  type: constants.STRING
};

const conditionConstraints = {
  variable: {
    type: constants.STRING,
    presence: { message: 'cannot be missing.' }
  },
  operator: {
    type: constants.STRING,
    presence: { message: 'cannot be missing.' },
    inclusion: {
      within: Object.values(constants.OPERATOR_TYPES),
      message: 'not saved'
    }
  },
  value: {
    type: constants.STRING,
    presence: { message: 'cannot be missing.' }
  }
};

const blockConstraints = {
  presence: true,
  condition: {
    type: constants.OBJECT,
    conditionFormatValidator: true
  }
};

const stepConstraints = {
  step: {
    presence: { message: 'attribute of the current step cannot be empty.' },
    type: constants.STRING
  },
  variable: {
    type: constants.STRING
  },
  next_step: {
    type: constants.STRING
  },
  condition: {
    type: constants.OBJECT,
    conditionFormatValidator: true
  },
  title: {
    type: constants.STRING,
    length: {
      maximum: constants.STEP_TITLE_MAX_LENGTH
    },
    format: {
      pattern: constants.NAME_REGEX,
      message: constants.NAME_PATTERN_ERROR
    }
  }
};

const editSearchFormConstraints = {
  query: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_MESSAGE_TO_QUERY_SEARCH_SKILL_LENGTH
    }
  },
  filter: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_MESSAGE_TO_FILTER_SEARCH_SKILL_LENGTH
    }
  }
};

const stepValidationConstraints = {
  errorResponse: {
    presence: { message: 'This field is required.' },
    type: constants.ARRAY,
    each: {
      type: constants.STRING,
      presence: true
    }
  },
  numberOfTurns: {
    type: constants.INTEGER
  }
};

const actionConstraints = {
  action: {
    presence: { message: 'action cannot be empty.' },
    type: constants.STRING,
    length: {
      maximum: constants.MAX_ACTION_LENGTH
    }
  },
  title: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_ACTION_TITLE_LENGTH
    }
  },
  steps: {
    type: constants.ARRAY,
    each: {
      validator: validateStep
    }
  }
};

const disambiguationSettingsConstraints = {
  prompt: {
    presence: { message: 'This field is required.' },
    length: {
      maximum: constants.DISAMBIGUATION_PROMPT_MAX_LENGTH,
      message: constants.DISAMBIGUATION_PROMPT_LENGTH_ERRROR
    }
  },
  noneOfAbovePrompt: {
    length: {
      maximum: constants.DISAMBIGUATION_NONE_OF_THE_ABOVE_MAX_LENGTH,
      message: constants.DISAMBIGUATION_PROMPT_LENGTH_ERRROR
    }
  },
  connectToSupportPrompt: {
    presence: { message: 'This field is required.' },
    length: {
      maximum: constants.DISAMBIGUATION_PROMPT_MAX_LENGTH,
      message: constants.DISAMBIGUATION_PROMPT_LENGTH_ERRROR
    }
  },
  singleAnswerClarificationPrompt: {
    presence: { message: 'This field is required.' },
    length: {
      maximum: constants.DISAMBIGUATION_PROMPT_MAX_LENGTH,
      message: constants.DISAMBIGUATION_PROMPT_LENGTH_ERRROR
    }
  }
};

const tagsConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateTag
  }
};

const tagConstraints = {
  presence: true,
  type: constants.STRING,
  length: {
    maximum: 64
  },
  format: {
    pattern: constants.NAME_REGEX,
    message: constants.NAME_PATTERN_ERROR
  }
};

const counterExamplesConstraints = {
  presence: false,
  type: constants.ARRAY,
  each: {
    validator: validateExample
  }
};

const examplesConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateExample
  }
};

const exampleConstraints = {
  text: {
    presence: true,
    type: constants.STRING,
    length: {
      maximum: constants.MAX_INTENT_EXAMPLE_LENGTH
    },
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  },
  entities: {
    type: constants.ARRAY,
    each: {
      validator: validateEntity
    }
  }
};

const entitiesConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateEntity
  }
};

const entityConstraints = {
  entity: {
    presence: { message: ' is required.' },
    type: constants.STRING,
    length: {
      maximum: 64
    },
    entityNameValidator: true
  },
  description: {
    type: constants.STRING,
    length: {
      maximum: 128
    },
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  },
  open_list: {
    type: constants.BOOLEAN
  },
  values: {
    type: constants.ARRAY,
    each: {
      validator: validateValue
    }
  }
};

const systemEntitiesConstraints = {
  presence: false,
  type: constants.ARRAY,
  each: {
    validator: validateSystemEntity
  }
};

const systemEntityConstraints = {
  entity: {
    presence: { message: 'name cannot be empty.' },
    type: constants.STRING,
    length: {
      maximum: 64
    },
    systemEntityNameValidator: true
  },
  description: {
    type: constants.STRING,
    length: {
      maximum: 128
    },
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  },
  open_list: {
    type: constants.BOOLEAN
  },
  values: {
    type: constants.ARRAY,
    each: {
      validator: validateValue
    }
  }
};

const valuesConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateValue
  }
};

const valueConstraints = {
  value: {
    presence: true,
    type: constants.STRING,
    length: {
      maximum: 64
    },
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  },
  metadata: {
    type: constants.OBJECT
  },
  synonyms: {
    type: constants.ARRAY,
    each: {
      validator: validateSynonym
    }
  },
  patterns: {
    type: constants.ARRAY,
    each: {
      validator: validatePattern
    }
  }
};

const synonymConstraints = {
  synonym: {
    presence: true,
    type: constants.STRING,
    length: {
      maximum: 64
    },
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  }
};

const patternConstraints = {
  pattern: {
    presence: true,
    type: constants.STRING,
    length: {
      maximum: 512
    },
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  }
};

const dialogConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateDialogNode
  }
};

const dialogNodeConstraints = {
  dialog_node: {
    presence: true,
    type: constants.STRING,
    length: {
      maximum: 1024
    },
    format: {
      pattern: constants.NODE_NAME_REGEX,
      message: constants.NAME_PATTERN_ERROR
    }
  },
  name: {
    type: constants.STRING,
    length: {
      maximum: 2048
    },
    format: {
      pattern: constants.NAME_REGEX,
      message: constants.NAME_PATTERN_ERROR
    }
  },
  description: {
    type: constants.STRING,
    length: {
      maximum: 128
    },
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  },
  conditions: {
    type: constants.STRING,
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  },
  parent: {
    type: constants.STRING,
    length: {
      maximum: 1024
    },
    format: {
      pattern: constants.NAME_REGEX,
      message: constants.NAME_PATTERN_ERROR
    }
  },
  previous_sibling: {
    type: constants.STRING,
    length: {
      maximum: 1024
    },
    format: {
      pattern: constants.NAME_REGEX,
      message: constants.NAME_PATTERN_ERROR
    }
  },
  output: {
    type: constants.OBJECT
  },
  context: {
    type: constants.OBJECT
  },
  type: {
    type: constants.STRING,
    length: {
      maximum: 1024
    },
    format: {
      pattern: constants.NAME_REGEX,
      message: constants.NAME_PATTERN_ERROR
    }
  },
  next_step: {
    type: constants.OBJECT
  },
  actions: {
    type: constants.ARRAY,
    length: {
      maximum: constants.ACTIONS_LIMIT,
      tooLong: 'needs to have %{count} actions or less'
    },
    each: {
      validator: validateCallout
    }
  },
  'next_step.dialog_node': {
    type: constants.STRING,
    length: {
      maximum: 1024
    },
    format: {
      pattern: constants.NAME_REGEX,
      message: constants.NAME_PATTERN_ERROR
    }
  },
  'next_step.selector': {
    type: constants.STRING,
    inclusion: {
      within: constants.SELECTORS,
      message:
        '^Go-to selectors that are currently supported are ' +
        constants.SELECTORS
    }
  },
  'next_step.behavior': {
    type: constants.STRING
  }
};

const outputFeaturesIfResponseTypesConstraints = {
  text: {
    types: [constants.STRING, constants.OBJECT, constants.ARRAY],
    ifType: {
      object: validateOutputTextAsObject,
      array: validateOutputTextAsArray
    }
  }
};

const outputFeaturesConstraints = {
  text: {
    types: [constants.STRING, constants.OBJECT],
    ifType: {
      object: validateOutputTextAsObject
    }
  }
};

const responseItemConstraints = {
  type: constants.STRING
};

const textResponseItemConstraints = {
  text: {
    required: true,
    type: constants.STRING,
    length: {
      maximum: constants.MAX_TEXT_LENGTH
    }
  }
};

const actionStepArtifactReferenceConstraints = {
  type: constants.STRING,
  length: {
    minimum: 1,
    maximum: 128
  },
  format: {
    pattern: constants.ACTIONS_STEPS_HANDLERS_NAME_REGEX
  }
};

const actionStepOutputConcatOperatorConstraints = {
  scalar: {
    types: [constants.STRING, constants.BOOLEAN, constants.NUMBER],
    length: {
      maximum: 400
    }
  },
  variable: actionStepArtifactReferenceConstraints,
  skill_variable: actionStepArtifactReferenceConstraints,
  expression: {
    type: constants.STRING,
    length: {
      minimum: 1
    }
  }
};

const actionStepTextResponseItemConstraints = {
  text: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_TEXT_LENGTH
    }
  },
  text_expression: {
    type: constants.OBJECT
  },
  'text_expression.concat': {
    type: constants.ARRAY,
    each: {
      validator: validateActionStepOutputConcatItem
    },
    length: {
      maximum: 10
    }
  }
};

const optionItemConstraints = {
  label: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_LABEL_LENGTH
    }
  },
  value: {
    type: constants.OBJECT
  },
  'value.input': {
    presence: true,
    type: constants.OBJECT
  },
  'value.input.text': {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_VALUE_LENGTH
    }
  }
};

const outputTextAsObjectConstraints = {
  values: {
    type: constants.ARRAY,
    each: {
      validator: validateResponseItem
    }
  },
  selection_policy: {
    type: constants.STRING
  }
};

const outputTextAsArrayConstraints = {
  type: constants.ARRAY,
  each: {
    validator: validateOutputTextAraryItem
  }
};

const advancedResponseConstraints = {
  output: dialogNodeConstraints.output,
  'output.generic': {
    type: constants.ARRAY,
    length: {
      maximum: constants.RESPONSES_LIMIT,
      tooLong: 'needs to have %{count} responses or less'
    },
    each: {
      validator: validateGenericResponseType
    }
  },
  context: dialogNodeConstraints.context,
  actions: dialogNodeConstraints.actions
};

const pauseResponseTypeConstraints = {
  time: {
    type: constants.NUMBER,
    numericality: {
      onlyInteger: true,
      noStrings: true,
      greaterThanOrEqualTo: 0,
      lessThanOrEqualTo: constants.MAX_TIME
    }
  },
  typing: {
    presence: true,
    type: constants.BOOLEAN
  }
};

const twilioSessionTimeoutConstraints = {
  timeout: {
    numericality: {
      onlyInteger: true,
      greaterThanOrEqualTo: 0,
      lessThanOrEqualTo: constants.MAX_TIME
    }
  }
};

const imageResponseTypeConstraints = {
  source: {
    required: true,
    type: constants.STRING,
    length: {
      maximum: constants.MAX_SOURCE_LENGTH
    }
  },
  description: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_DESCRIPTION_LENGTH
    }
  },
  title: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_TITLE_LENGTH
    }
  }
};

const optionResponseTypeConstraints = {
  title: {
    required: true,
    type: constants.STRING,
    length: {
      maximum: constants.MAX_TITLE_LENGTH
    }
  },
  description: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_DESCRIPTION_LENGTH
    }
  },
  options: {
    type: constants.ARRAY,
    required: true,
    length: {
      maximum: constants.OPTIONS_LIMIT,
      tooLong: 'needs to have %{count} options or less'
    },
    each: {
      validator: validateOptionItem
    }
  }
};

const actionsOptionResponseTypeConstraints = {
  options: {
    type: constants.ARRAY,
    required: true,
    length: {
      maximum: constants.OPTIONS_LIMIT,
      tooLong: 'needs to have %{count} options or less'
    },
    each: {
      validator: validateOptionItem
    }
  }
};

const connectToAgentResponseTypeConstraints = {
  agent_available: {
    type: constants.OBJECT
  },
  'agent_available.message': {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_AGENT_AVAILABILITY_MESSAGE_LENGTH
    }
  },
  agent_unavailable: {
    type: constants.OBJECT
  },
  'agent_unavailable.message': {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_AGENT_AVAILABILITY_MESSAGE_LENGTH
    }
  },
  message_to_human_agent: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_MESSAGE_TO_HUMAN_AGENT_LENGTH
    }
  },
  transfer_info: {
    type: constants.OBJECT
  },
  'transfer_info.target': {
    type: constants.OBJECT
  }
};

const channelTransferResponseTypeConstraints = {
  message_to_user: {
    required: true,
    presence: { message: 'cannot be empty.' },
    type: constants.STRING,
    length: {
      maximum: constants.MAX_CHANNEL_TRANSFER_MESSAGE_TO_USER_LENGTH,
      tooLong: constants.CHANNEL_TRANSFER_MESSAGE_TO_USER_MAX
    }
  },
  transfer_info: {
    required: true,
    type: constants.OBJECT
  },
  'transfer_info.target': {
    required: true,
    type: constants.OBJECT
  },
  'transfer_info.target.chat': {
    required: true,
    type: constants.OBJECT
  },
  'transfer_info.target.chat.url': {
    required: true,
    type: constants.STRING,
    url: {
      schemes: ['https']
    }
  }
};

const searchSkillResponseTypeConstraints = {
  query: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_MESSAGE_TO_QUERY_SEARCH_SKILL_LENGTH
    }
  },
  filter: {
    type: constants.STRING,
    length: {
      maximum: constants.MAX_MESSAGE_TO_FILTER_SEARCH_SKILL_LENGTH
    }
  },
  query_type: {
    presence: true,
    type: constants.STRING,
    inclusion: {
      within: constants.ALLOWED_QUERY_TYPES_SEARCH_SKILL,
      message:
        '^Search skill query types that are currently supported are: ' +
        constants.ALLOWED_QUERY_TYPES_SEARCH_SKILL
    }
  }
};

const textResponseTypeConstraints = {
  values: {
    type: constants.ARRAY,
    length: {
      maximum: constants.VARIATIONS_LIMIT,
      tooLong: 'needs to have %{count} items or less'
    },
    each: {
      validator: validateTextResponseItem
    }
  }
};

const actionStepTextResponseTypeConstraints = {
  values: {
    type: constants.ARRAY,
    length: {
      maximum: constants.VARIATIONS_LIMIT,
      tooLong: 'needs to have %{count} items or less'
    },
    each: {
      validator: validateActionStepTextResponseItem
    }
  }
};

const responsesLengthConstraints = {
  length: {
    maximum: constants.RESPONSES_LIMIT - 1
  }
};

const optionsLengthConstraints = {
  length: {
    maximum: constants.OPTIONS_LIMIT - 1
  }
};

const responseTypeConstraints = {
  response_type: {
    presence: true,
    type: constants.STRING
  }
};

//-------- actions output constraints start --------
const actionStepResponseConstraints = {
  output: dialogNodeConstraints.output,
  'output.generic': {
    type: constants.ARRAY,
    length: {
      maximum: constants.RESPONSES_LIMIT,
      tooLong: 'needs to have %{count} responses or less'
    },
    each: {
      validator: validateActionStepGenericResponseType
    }
  },
  context: dialogNodeConstraints.context
};
//-------- actions output constraints end --------

const regionConstraints = {
  presence: true,
  type: constants.STRING,
  length: {
    maximum: 16
  },
  format: {
    pattern: constants.REGION_REGEX,
    message: constants.REGION_PATTERN_ERROR
  }
};

const guidsConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateGuid
  }
};

const guidConstraints = {
  presence: true,
  type: constants.STRING,
  format: {
    pattern: constants.GUID_REGEX,
    message: constants.GUID_PATTERN_ERROR
  }
};

const utteranceConstraints = {
  input: {
    presence: true,
    type: constants.OBJECT
  },
  'input.text': {
    type: constants.STRING,
    format: {
      pattern: constants.TEXT_REGEX,
      message: constants.TEXT_PATTERN_ERROR
    }
  },
  context: {
    type: constants.OBJECT
  }
};

const dateConstraints = {
  presence: true,
  type: constants.STRING,
  format: {
    pattern: constants.DATE_REGEX,
    message: constants.DATE_PATTERN_ERROR
  }
};

const logDateConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateDate
  }
};

const logTimeConstraints = {
  presence: true,
  type: constants.ARRAY,
  each: {
    validator: validateTime
  }
};

const timeConstraints = {
  presence: true,
  type: constants.STRING,
  format: {
    pattern: constants.TIME_REGEX,
    message: constants.TIME_PATTERN_ERROR
  }
};

const versionDateConstraints = {
  presence: true,
  type: constants.STRING,
  format: {
    pattern: constants.VERSION_DATE_REGEX,
    message: constants.VERSION_PATTERN_ERROR
  }
};

const contextVariableNameConstraints = {
  presence: true,
  type: constants.STRING,
  length: {
    maximum: constants.MAX_CONTEXT_VAR_LENGTH
  },
  format: {
    pattern: constants.CONTEXT_VARIABLE_NAME_REGEX,
    message: constants.CONTEXT_VARIABLE_NAME_ERROR
  }
};

const stepTitleConstraints = {
  type: constants.STRING,
  length: {
    maximum: constants.STEP_TITLE_MAX_LENGTH,
    message: constants.STEP_TITLE_MAX_LENGTH_ERROR
  },
  format: {
    pattern: constants.NAME_REGEX,
    message: constants.NAME_PATTERN_ERROR
  }
};

const phoneNumberConstraints = {
  phone_number: {
    type: constants.STRING,
    length: {
      maximum: 30
    },
    format: {
      pattern: constants.PHONE_EDIT_REGEX,
      message: constants.PHONE_EDIT_ERROR
    }
  }
};

const phoneSaveConstraints = {
  phone_number: {
    type: constants.STRING,
    length: {
      maximum: 30
    },
    format: {
      pattern: constants.PHONE_NORMALIZED_REGEX,
      message: constants.PHONE_NORMALIZED_ERROR
    }
  }
};

const phoneDescriptionConstraints = {
  description: {
    type: constants.STRING,
    length: {
      maximum: 128
    },
    format: {
      pattern: constants.NAME_REGEX,
      message: constants.NAME_PATTERN_ERROR
    }
  }
};

const coexistenceTitleConstraints = {
  presence: true,
  type: constants.STRING,
  length: {
    maximum: constants.MAX_ACTION_TITLE_LENGTH
  }
};

const phoneMessageFailureConstraints = {
  message: {
    presence: true,
    length: {
      maximum: 150
    }
  }
};

const sipUriConstraints = {
  sip_uri: {
    length: {
      maximum: 150
    },
    format: {
      pattern: constants.SIP_PATTERN_REGEX,
      message: constants.SIP_PATTERN_ERROR
    }
  }
};
const telUriConstraints = {
  sip_uri: {
    format: {
      pattern: constants.TEL_PATTERN_REGEX,
      message: constants.TEL_PATTERN_ERROR
    }
  }
};

const sipStartConstraints = {
  sip_uri: {
    format: {
      pattern: constants.SIP_URI_REGEX,
      message: constants.SIP_URI_ERROR
    }
  }
};

const sipHeaderConstraints = {
  sip_header: {
    format: {
      pattern: constants.SIP_HEADER_REGEX,
      message: constants.SIP_HEADER_ERROR
    }
  }
};

export function validateGenericResponseType(response, options) {
  switch (response.response_type) {
    case constants.RESPONSE_TYPES.pause:
      return validatePauseResponseType(response, options);
    case constants.RESPONSE_TYPES.text:
      return validateTextResponseType(response, options);
    case constants.RESPONSE_TYPES.image:
      return validateImageResponseType(response, options);
    case constants.RESPONSE_TYPES.connect_to_agent:
      return validateConnectToAgentResponseType(response, options);
    case constants.RESPONSE_TYPES.channel_transfer:
      return validateChannelTransferResponseType(response, options);
    case constants.RESPONSE_TYPES.option:
      return validateOptionResponseType(response, options);
    case constants.RESPONSE_TYPES.search_skill:
      return validateSearchSkillResponseType(response, options);
    default:
      return validateResponseType(response, options);
  }
}

export function validateActionStepGenericResponseType(response, options) {
  switch (response.response_type) {
    case constants.RESPONSE_TYPES.pause:
      return validatePauseResponseType(response, options);
    case constants.RESPONSE_TYPES.text:
      return validateActionStepTextResponseType(response, options);
    case constants.RESPONSE_TYPES.image:
      return validateImageResponseType(response, options);
    case constants.RESPONSE_TYPES.connect_to_agent:
      return validateConnectToAgentResponseType(response, options);
    case constants.RESPONSE_TYPES.option:
      return validateActionsOptionResponseType(response, options);
    case constants.RESPONSE_TYPES.search_skill:
      return validateSearchSkillResponseType(response, options);
    default:
      return validateResponseType(response, options);
  }
}

export function validatePauseResponseType(response, options) {
  return validate(response, pauseResponseTypeConstraints, options);
}

export function validateImageResponseType(response, options) {
  return validate(response, imageResponseTypeConstraints, options);
}

export function validateOptionResponseType(response, options) {
  return validate(response, optionResponseTypeConstraints, options);
}

function validateActionsOptionResponseType(response, options) {
  return validate(response, actionsOptionResponseTypeConstraints, options);
}

function validateOptionItem(response, options) {
  return validate(response, optionItemConstraints, options);
}

export function validateConnectToAgentResponseType(response, options) {
  return validate(response, connectToAgentResponseTypeConstraints, options);
}

export function validateChannelTransferResponseType(response, options) {
  return validate(response, channelTransferResponseTypeConstraints, options);
}

export function validateSearchSkillResponseType(response, options) {
  return validate(response, searchSkillResponseTypeConstraints, options);
}

export function validateTextResponseType(response, options) {
  return validate(response, textResponseTypeConstraints, options);
}

export function validateActionStepTextResponseType(response, options) {
  return validate(response, actionStepTextResponseTypeConstraints, options);
}

export function responsesLimitReached(responses, options) {
  return validate.single(responses, responsesLengthConstraints, options);
}

export function optionsLimitReached(responseOptions, options) {
  return validate.single(responseOptions, optionsLengthConstraints, options);
}

function validateResponseType(response, options) {
  return validate(response, responseTypeConstraints, options);
}

function validateOutputTextAsObject(text) {
  return validate(text, outputTextAsObjectConstraints);
}

function validateOutputTextAsArray(text) {
  return validate.single(text, outputTextAsArrayConstraints);
}

function validateTextResponseItem(item) {
  return validate(item, textResponseItemConstraints);
}

function validateActionStepTextResponseItem(item) {
  return validate(item, actionStepTextResponseItemConstraints);
}

function validateActionStepOutputConcatItem(item) {
  return validate(item, actionStepOutputConcatOperatorConstraints);
}

function validateResponseItem(item) {
  return validate.single(item, responseItemConstraints);
}

function validateOutputTextAraryItem(item) {
  return validate.single(item, responseItemConstraints);
}

function validateCallout(action) {
  return validate(action, calloutConstraints);
}

export function validateWorkspaces(workspaces) {
  return validate.single(workspaces, workspacesConstraints);
}

export function validateWorkspace(workspace) {
  if (!validate.isDefined(workspace)) {
    return { root: 'Workspace must exist.' };
  } else if (!validate.isHash(workspace)) {
    return { root: 'Workspace must be an object.' };
  } else {
    return validate(workspace, workspaceConstraints);
  }
}

export function validateIntents(intents) {
  return validate.single(intents, intentsConstraints);
}

export function validateIntent(intent) {
  if (!validate.isDefined(intent)) {
    return { root: 'Intent must exist.' };
  } else if (!validate.isHash(intent)) {
    return { root: 'Intent must be an object.' };
  } else {
    return validate(intent, intentConstraints);
  }
}

export function validateCondition(condition) {
  if (!validate.isDefined(condition)) {
    return { root: 'Condition must exist.' };
  } else if (!validate.isHash(condition)) {
    return { root: 'Condition must be an object.' };
  } else {
    return validate(condition, conditionConstraints, { format: 'flat' });
  }
}

export function validateStep(step) {
  if (!validate.isDefined(step)) {
    return { root: 'Step must exist.' };
  } else if (!validate.isHash(step)) {
    return { root: 'Step must be an object.' };
  } else {
    return validate(step, stepConstraints, { format: 'flat' });
  }
}

export function validateBlock(block) {
  if (!validate.isDefined(block)) {
    return { root: 'Block must exist.' };
  } else if (!validate.isArray(block)) {
    return { root: 'Block must be an object.' };
  } else {
    let blockErrors = block.reduce((errors, item) => {
      const error = validate({ condition: item }, blockConstraints, {
        format: 'flat'
      });
      if (error) {
        errors.push(error);
      }
      return errors;
    }, []);

    blockErrors = _.flatten(blockErrors);
    if (blockErrors) {
      blockErrors = blockErrors.filter(
        (error, index) => blockErrors.indexOf(error) === index
      );
    }
    return blockErrors;
  }
}

export function validateAction(action) {
  if (!validate.isDefined(action)) {
    return { root: 'Action must exist.' };
  } else if (!validate.isHash(action)) {
    return { root: 'Action must be an object.' };
  } else {
    return validate(action, actionConstraints);
  }
}

export function validateDisambiguationSettings(
  disambiguationSettings,
  options
) {
  return validate(
    disambiguationSettings,
    disambiguationSettingsConstraints,
    options
  );
}

export function validateTags(tags) {
  return validate.single(tags, tagsConstraints);
}

export function validateTag(tag) {
  return validate.single(tag, tagConstraints);
}

export function validateExamples(examples) {
  return validate.single(examples, examplesConstraints);
}

export function validateCounterExamples(counterExamples) {
  return validate.single(counterExamples, counterExamplesConstraints);
}

export function validateExample(example) {
  if (!validate.isDefined(example)) {
    return { root: 'Example must exist.' };
  } else if (!validate.isHash(example)) {
    return { root: 'Example must be an object.' };
  } else {
    return validate(example, exampleConstraints);
  }
}

export function validateEntities(entities) {
  return validate.single(entities, entitiesConstraints);
}

export function validateEntity(entity) {
  if (!validate.isDefined(entity)) {
    return { root: 'Entity must exist.' };
  } else if (!validate.isHash(entity)) {
    return { root: 'Entity must be an object.' };
  } else {
    return validate(entity, entityConstraints);
  }
}

export function validateSystemEntities(entities) {
  return validate.single(entities, systemEntitiesConstraints);
}

export function validateSystemEntity(entity) {
  if (!validate.isDefined(entity)) {
    return { root: 'Entity must exist.' };
  } else if (!validate.isHash(entity)) {
    return { root: 'Entity must be an object.' };
  } else {
    return validate(entity, systemEntityConstraints);
  }
}

export function validateValues(values) {
  return validate.single(values, valuesConstraints);
}

export function validateValue(value) {
  if (!validate.isDefined(value)) {
    return { root: 'Value must exist.' };
  } else if (!validate.isHash(value)) {
    return { root: 'Value must be an object.' };
  } else {
    return validate(value, valueConstraints);
  }
}

export function validateSynonym(synonym) {
  return validate({ synonym: synonym }, synonymConstraints);
}

export function validatePattern(pattern) {
  return validate({ pattern: pattern }, patternConstraints);
}

export function validateDialog(nodes) {
  return validate.single(nodes, dialogConstraints);
}

function countResponseTypeUsage(responseTypes, type) {
  return responseTypes.filter(responseType => {
    return responseType.response_type === type;
  }).length;
}

export function validateResponseTypeOnlyOneUsage(advancedResponse, type) {
  if (
    advancedResponse &&
    advancedResponse.output &&
    advancedResponse.output.generic &&
    advancedResponse.output.generic.length > 0
  ) {
    const usageCount = countResponseTypeUsage(
      advancedResponse.output.generic,
      type
    );
    if (usageCount > 1) {
      return { 'output.generic': type + ' used more then once.' };
    }
  }
  return null;
}

export function validateAdvancedResponse(advancedResponse, options) {
  if (!validate.isHash(advancedResponse)) {
    return { root: ' Advanced response must be an object.' };
  } else if (
    !_.isEmpty(
      _.difference(
        _.keys(advancedResponse),
        _.keys(advancedResponseConstraints)
      )
    )
  ) {
    return {
      root:
        ' Response can only contain properties: ' +
        _.join(_.keys(advancedResponseConstraints), ', ')
    };
  } else {
    const searchSkillOnlyOne = validateResponseTypeOnlyOneUsage(
      advancedResponse,
      'search_skill'
    );
    const connectToAgentOnlyOne = validateResponseTypeOnlyOneUsage(
      advancedResponse,
      'connect_to_agent'
    );
    const channelTransferOnlyOne = validateResponseTypeOnlyOneUsage(
      advancedResponse,
      'channel_transfer'
    );
    return (
      searchSkillOnlyOne ||
      connectToAgentOnlyOne ||
      channelTransferOnlyOne ||
      validate(advancedResponse, advancedResponseConstraints, options || {})
    );
  }
}

export function validateActionStepResponse(response, options) {
  const fullResponse = { output: response }; // should remove this line once we allow editing context in actions
  if (!validate.isHash(fullResponse)) {
    return { root: ' Advanced response must be an object.' };
  } else if (
    !_.isEmpty(
      _.difference(_.keys(fullResponse), _.keys(actionStepResponseConstraints))
    )
  ) {
    return {
      root:
        ' Response can only contain properties: ' +
        _.join(_.keys(actionStepResponseConstraints), ', ')
    };
  } else {
    const searchSkillOnlyOne = validateResponseTypeOnlyOneUsage(
      fullResponse,
      'search_skill'
    );
    const connectToAgentOnlyOne = validateResponseTypeOnlyOneUsage(
      fullResponse,
      'connect_to_agent'
    );
    return (
      searchSkillOnlyOne ||
      connectToAgentOnlyOne ||
      validate(fullResponse, actionStepResponseConstraints, options || {})
    );
  }
}

export function validateOutputFeaturesIfResponseTypes(output, options) {
  return validate(
    output,
    outputFeaturesIfResponseTypesConstraints,
    options || {}
  );
}

export function validateOutputFeatures(output, options) {
  return validate(output, outputFeaturesConstraints, options || {});
}

export function validateDialogNode(node) {
  if (!validate.isDefined(node)) {
    return { root: 'Node must exist.' };
  } else if (!validate.isHash(node)) {
    return { root: 'Node must be an object.' };
  } else {
    return validate(node, dialogNodeConstraints);
  }
}

export function validateRegion(region) {
  return validate.single(region, regionConstraints);
}

export function validateGuids(guids) {
  return validate.single(guids, guidsConstraints);
}

export function validateGuid(guid) {
  return validate.single(guid, guidConstraints);
}

export function validateUtterance(utterance) {
  if (!validate.isDefined(utterance)) {
    return { root: 'Utterance must exist.' };
  } else if (!validate.isHash(utterance)) {
    return { root: 'Utterance must be an object.' };
  } else {
    return validate(utterance, utteranceConstraints);
  }
}

export function validateLogDateRange(dates) {
  return validate.single(dates, logDateConstraints);
}

export function validateRange(range) {
  var error = '';
  if (validate.isDefined(range.start) && !validate.isInteger(range.start)) {
    error = 'Start must be an integer';
  }
  if (validate.isDefined(range.count) && !validate.isInteger(range.count)) {
    if (error.length) {
      error = error + ' and ';
    }
    error = error + 'Count must be an integer';
  }
  if (error.length) {
    return error;
  } else {
    return null;
  }
}

export function validateDate(date) {
  if (!validate.isDefined(date)) {
    return { root: 'Date must exist.' };
  } else {
    var parsedDate = Date.parse(date);
    if (_.isNaN(parsedDate)) {
      return { root: 'Invalid date.' };
    } else {
      return validate.single(date, dateConstraints);
    }
  }
}

export function validateVersionDate(date) {
  if (!validate.isDefined(date)) {
    return { root: 'Version date must exist.' };
  } else {
    var parsedDate = Date.parse(date);
    if (_.isNaN(parsedDate)) {
      return { root: 'Invalid date.' };
    } else {
      return validate.single(date, versionDateConstraints);
    }
  }
}

export function validateLogTimeRange(times) {
  return validate.single(times, logTimeConstraints);
}

/*eslint complexity: [2, 9] */
export function validateTime(time) {
  if (!validate.isDefined(time)) {
    return { root: 'Time must exist.' };
  } else {
    return validate.single(time, timeConstraints);
  }
}

export function validateTimeslice(timeslice) {
  if (!validate.isDefined(timeslice)) {
    return { root: 'Timeslice must exist.' };
  } else if (
    timeslice !== 'day' &&
    timeslice !== 'week' &&
    timeslice !== 'month' &&
    timeslice !== 'quarter' &&
    timeslice !== 'year'
  ) {
    return { root: 'Invalid timeslice value.' };
  } else {
    return null;
  }
}

export function validateContextVariableName(name) {
  return validate.single(name, contextVariableNameConstraints);
}

export function validateStepTitle(title) {
  return validate.single(title, stepTitleConstraints);
}

export function validatePhoneNumber(phonenumber) {
  return validate(phonenumber, phoneNumberConstraints);
}

export function validateNormalizedPhone(phonenumber) {
  return validate(phonenumber, phoneSaveConstraints);
}

export function validatePhoneDescription(description) {
  return validate(description, phoneDescriptionConstraints);
}

export function validateCoexistenceTitle(title) {
  return validate.single(title, coexistenceTitleConstraints);
}

export function format(result) {
  if (_.isNil(result)) {
    return result;
  }
  var formatedResult = {};
  for (const key in result) {
    formatedResult[key] = deepFormat(result[key]);
  }
  return formatedResult;
}

function deepFormat(result) {
  if (_.isObject(result) || _.isArray(result)) {
    var arr = [];
    for (const key in result) {
      arr.push(deepFormat(result[key]));
    }
    return arr.join(' ');
  } else {
    return result;
  }
}

export function validateURL(url, schemes) {
  if (schemes) {
    return validate(
      { website: url },
      {
        website: {
          url: {
            schemes
          }
        }
      }
    );
  }
  return validate({ website: url }, { website: { url: true } });
}

export function validateOptions(optionItems, options) {
  return validate.single(optionItems, optionsConstraints, options);
}

export function validateOptionListItem(item, options) {
  return validate(item, optionConstraints, options);
}

export function validateOptionSynonym(item, options) {
  return validate.single(item, optionSynonymConstraints, options);
}

export function validateStepValidation(stepValidation, options) {
  return validate(stepValidation, stepValidationConstraints, options);
}

export function validateEditSearchForm(searchFormUpdate, options) {
  return validate(searchFormUpdate, editSearchFormConstraints, options);
}

export function validateTwilioSessionTimeout(value) {
  return validate({ timeout: value }, twilioSessionTimeoutConstraints);
}

export function validatePhoneMessageLength(value) {
  return validate({ message: value }, phoneMessageFailureConstraints);
}

export function validateSipURI(value) {
  return validate({ sip_uri: value }, sipUriConstraints);
}

export function validateSipStart(value) {
  return validate({ sip_uri: value }, sipStartConstraints);
}

export function validateTelURI(value) {
  return validate({ sip_uri: value }, telUriConstraints);
}

export function validateSipURIField(value) {
  if (!validateSipStart(value)) {
    if (/^tel/.test(value)) {
      return validateTelURI(value);
    } else {
      return validateSipURI(value);
    }
  } else {
    return validateSipStart(value);
  }
}

export function validateSipHeader(value) {
  return validate({ sip_header: value }, sipHeaderConstraints);
}
